import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../shared/AuthContext';
import { api, getThumbUrl, getBaseUrl } from '../../shared/api';
import { authService } from '../../shared/authService';
import { useChatSocket } from '../../shared/useChatSocket';
import { COLOR_SUCCESS, COLOR_SECONDARY, getContrastColor } from '../../shared/colorUtils';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import '../assistant/AssistantChat.css';

const PAGE_SIZE = 50;

// ── Image generation defaults ───────────────────────────────────────────────
const DEFAULT_IMAGE_SIZE = '1024x1024';

// ── Slash commands registry ──────────────────────────────────────────────────
const SLASH_COMMANDS = [
  { command: '/assistant', description: 'chat.cmd.assistant' },
  { command: '/generate',  description: 'chat.cmd.generate'  },
  { command: '/edit',      description: 'chat.cmd.edit'      },
];

const ChatRoom = ({ roomId, roomInfo, onRoomsRefresh }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [inputText, setInputText] = useState('');

  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [sendingImage, setSendingImage] = useState(false);

  const [slashIndex, setSlashIndex] = useState(0);
  const [imageSize, setImageSize] = useState(DEFAULT_IMAGE_SIZE);
  const [editImageSize, setEditImageSize] = useState(DEFAULT_IMAGE_SIZE);

  // ── Group edit modal ─────────────────────────────────────────────────────
  const [showEditModal, setShowEditModal] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editPhotoFile, setEditPhotoFile] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const editPhotoInputRef = useRef(null);

  // Filtered suggestions — only shown when input starts with /
  const slashSuggestions = useMemo(() => {
    // Only show while the user is still typing the command word — once a space
    // is present they've moved on to the argument, so hide the popup.
    if (!inputText.startsWith('/') || inputText.includes(' ')) return [];
    return SLASH_COMMANDS.filter((c) => c.command.startsWith(inputText.toLowerCase()));
  }, [inputText]);

  const showSizePicker     = /^\/generate(\s|$)/i.test(inputText);
  const showEditSizePicker  = /^\/edit(\s|$)/i.test(inputText);

  const messagesEndRef  = useRef(null);
  const textareaRef     = useRef(null);
  const fileInputRef    = useRef(null);
  const typingTimers    = useRef({});
  const shouldScrollRef = useRef(true);
  const lastMessageIdRef = useRef(null); // tracks last known message for read receipts
  const messagesRef = useRef(messages);   // always-current snapshot for handleSend
  const pendingAiBubbleRef = useRef(null); // temp ID of local AI bubble awaiting DB save

  // Keep messagesRef in sync without adding messages to handleSend deps
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Clear all typing timers on unmount to prevent state updates on an unmounted component
  useEffect(() => () => { Object.values(typingTimers.current).forEach(clearTimeout); }, []);

  // ── Mark room as read (REST) ──────────────────────────────────────────────
  // Uses the REST endpoint so it works regardless of WebSocket state and covers
  // messages that arrived while the room was not open or the window was blurred.
  const markRoomRead = useCallback(async () => {
    const messageId = lastMessageIdRef.current;
    if (!messageId) return;
    try {
      await api.post(`chat/rooms/${roomId}/mark_read/`, { message_id: messageId });
      onRoomsRefresh?.();
    } catch (_) {
      /* non-blocking */
    }
  }, [roomId, onRoomsRefresh]);

  // ── Load history ────────────────────────────────────────────────────────

  const loadMessages = useCallback(async (pg = 1, prepend = false) => {
    try {
      const data = await api.getRaw(`chat/rooms/${roomId}/messages/?page=${pg}&page_size=${PAGE_SIZE}`);
      const msgs = data.results ?? [];
      setMessages((prev) => prepend ? [...msgs, ...prev] : msgs);
      setHasMore(data.has_more ?? false);
      setPage(pg);
      // Track the last message for read-receipt; only update on initial/refresh loads,
      // not when prepending older history ("load more" going backwards).
      if (!prepend && msgs.length > 0) {
        lastMessageIdRef.current = msgs[msgs.length - 1].id;
      }
    } catch (_) {
      /* non-blocking */
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [roomId]);

  useEffect(() => {
    setLoading(true);
    setMessages([]);
    setPage(1);
    loadMessages(1);
  }, [loadMessages]);

  // Scroll to bottom on initial load and new messages
  useEffect(() => {
    if (shouldScrollRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [messages]);

  // ── WebSocket events ─────────────────────────────────────────────────────

  const handleWsEvent = useCallback((event) => {
    if (event.type === 'message') {
      shouldScrollRef.current = true;
      // Capture pending refs before entering the state updater
      const pendingAiTemp = pendingAiBubbleRef.current;
      setMessages((prev) => {
        if (prev.some((m) => m.id === event.id)) return prev;
        // SYSTEM or IMAGE from AI: replace pending local streaming bubble if present
        if ((event.message_type === 'SYSTEM' || event.message_type === 'IMAGE') && pendingAiTemp) {
          const idx = prev.findIndex((m) => m.id === pendingAiTemp);
          if (idx !== -1) {
            const next = [...prev];
            next[idx] = event;
            return next;
          }
        }
        return [...prev, event];
      });
      // Clear pending ref after scheduling the update
      if ((event.message_type === 'SYSTEM' || event.message_type === 'IMAGE') && pendingAiBubbleRef.current) {
        pendingAiBubbleRef.current = null;
      }
      lastMessageIdRef.current = event.id;
      if (document.hasFocus()) {
        markRoomRead();
      }
      onRoomsRefresh?.();
    } else if (event.type === 'typing') {
      const uid = event.user_id;
      if (uid === user?.id) return;
      setTypingUsers((prev) => new Set([...prev, uid]));
      // Auto-clear after 3s
      if (typingTimers.current[uid]) clearTimeout(typingTimers.current[uid]);
      typingTimers.current[uid] = setTimeout(() => {
        setTypingUsers((prev) => { const s = new Set(prev); s.delete(uid); return s; });
      }, 3000);
    }
  }, [user?.id, onRoomsRefresh, markRoomRead]);

  const { connected, sendMessage, sendTyping } = useChatSocket(roomId, handleWsEvent);

  // ── File / drag-and-drop ───────────────────────────────────────────────

  const handleFilesAdded = useCallback((fileList) => {
    const images = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
    images.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachedFiles((prev) => {
          if (prev.length >= 5) return prev;
          return [...prev, { id: `${Date.now()}-${Math.random()}`, file, dataUrl: reader.result }];
        });
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    handleFilesAdded(e.dataTransfer.files);
  }, [handleFilesAdded]);

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles = Array.from(items)
      .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (imageFiles.length > 0) handleFilesAdded(imageFiles);
  }, [handleFilesAdded]);

  // ── Image SSE stream helper ───────────────────────────────────────────────
  const streamImageResponse = useCallback(async (resp, aiBubbleId) => {
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let imageUrl = null;
    let imagePrompt = null;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        let event;
        try { event = JSON.parse(line.slice(6)); } catch { continue; }
        if (event.type === 'image') {
          imageUrl = event.url;
          imagePrompt = event.prompt;
          shouldScrollRef.current = true;
          setMessages((prev) => prev.map((m) =>
            m.id === aiBubbleId
              ? { ...m, _imageUrl: imageUrl, _imagePrompt: imagePrompt, _streaming: false }
              : m
          ));
        } else if (event.type === 'done') {
          setMessages((prev) => prev.map((m) =>
            m.id === aiBubbleId ? { ...m, _streaming: false } : m
          ));
        } else if (event.type === 'error') {
          setMessages((prev) => prev.map((m) =>
            m.id === aiBubbleId ? { ...m, content: `⚠️ ${event.message}`, _isImage: false, _streaming: false } : m
          ));
        }
      }
    }
    return imageUrl ? { imageUrl, imagePrompt } : null;
  }, []);

  // ── Send ─────────────────────────────────────────────────────────────────

  const handleSend = useCallback(async () => {
    const text = inputText.trim();

    // /assistant command — broadcast question to room, stream AI answer, persist response
    const ASSISTANT_CMD = /^\/assistant\s*/i;
    if (attachedFiles.length === 0 && ASSISTANT_CMD.test(text)) {
      const query = text.replace(ASSISTANT_CMD, '').trim();
      if (!query) return;
      setInputText('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      shouldScrollRef.current = true;

      // Collect last 20 real text messages as context for the AI
      const chatHistory = messagesRef.current
        .filter((m) => !m._isAi && m.message_type !== 'SYSTEM' && m.message_type !== 'IMAGE' && m.content)
        .slice(-20)
        .map((m) => ({
          role: m.sender?.id === user?.id ? 'user' : 'assistant',
          content: m.content,
        }));

      // Broadcast user question to room — saves to DB and visible to all members
      sendMessage(query);

      // Show local streaming AI bubble immediately
      const aiBubbleId = `ai-resp-${Date.now()}`;
      pendingAiBubbleRef.current = aiBubbleId;
      setMessages((prev) => [
        ...prev,
        { id: aiBubbleId, _isAi: true, _aiRole: 'assistant', content: '', _streaming: true, created_at: new Date().toISOString(), sender: null },
      ]);

      let fullResponse = '';
      try {
        try {
          const expires = authService.getTokenExpiry?.();
          if (expires && Date.now() / 1000 >= expires - 300) await authService.refreshToken();
        } catch (_) { /* continue — server will 401 if truly expired */ }
        const resp = await fetch(`${getBaseUrl()}/assistant/api/stream/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: query, chat_history: chatHistory, source: 'chat_inline' }),
          credentials: 'include',
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            let event;
            try { event = JSON.parse(line.slice(6)); } catch { continue; }
            if (event.type === 'token') {
              fullResponse += event.content;
              shouldScrollRef.current = true;
              setMessages((prev) => prev.map((m) =>
                m.id === aiBubbleId ? { ...m, content: m.content + event.content } : m
              ));
            } else if (event.type === 'done') {
              setMessages((prev) => prev.map((m) =>
                m.id === aiBubbleId ? { ...m, _streaming: false } : m
              ));
            } else if (event.type === 'error') {
              setMessages((prev) => prev.map((m) =>
                m.id === aiBubbleId ? { ...m, content: `⚠️ ${event.message}`, _streaming: false } : m
              ));
            }
          }
        }
      } catch (err) {
        setMessages((prev) => prev.map((m) =>
          m.id === aiBubbleId ? { ...m, content: `⚠️ ${err.message}`, _streaming: false } : m
        ));
        pendingAiBubbleRef.current = null;
        return;
      }

      // Persist the complete response — broadcasts to all room members via WS
      if (fullResponse) {
        try {
          const savedMsg = await api.post(`chat/rooms/${roomId}/send_ai_response/`, { content: fullResponse });
          // Replace local bubble with DB-persisted message; WS echo will dedup by real id
          setMessages((prev) => prev.map((m) => m.id === aiBubbleId ? savedMsg : m));
          pendingAiBubbleRef.current = null;
        } catch (_) {
          pendingAiBubbleRef.current = null;
          // Non-blocking: local bubble remains visible if save fails
        }
      } else {
        pendingAiBubbleRef.current = null;
      }
      return;
    }

    // /generate <prompt>: text-to-image via assistant API, rendered inline
    const GENERATE_CMD = /^\/generate\s*/i;
    if (GENERATE_CMD.test(text)) {
      const prompt = text.replace(GENERATE_CMD, '').trim();
      if (!prompt) return;
      setInputText('');
      setAttachedFiles([]);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      shouldScrollRef.current = true;

      // Broadcast the command so it appears in all members' history
      sendMessage(text);

      const aiBubbleId = `ai-img-${Date.now()}`;
      pendingAiBubbleRef.current = aiBubbleId;
      setMessages((prev) => [
        ...prev,
        { id: aiBubbleId, _isAi: true, _aiRole: 'assistant', _isImage: true, content: '', _streaming: true, created_at: new Date().toISOString(), sender: null },
      ]);

      try {
        const resp = await fetch(`${getBaseUrl()}/assistant/api/generate-image/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt, size: imageSize }),
          credentials: 'include',
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const result = await streamImageResponse(resp, aiBubbleId);
        if (result?.imageUrl) {
          try {
            const savedMsg = await api.post(`chat/rooms/${roomId}/save_ai_image/`, {
              image_url: result.imageUrl,
              prompt: result.imagePrompt || '',
            });
            setMessages((prev) => prev.map((m) => m.id === aiBubbleId ? savedMsg : m));
          } catch (saveErr) {
            console.error('[/generate] save_ai_image failed:', saveErr);
            /* non-blocking — local bubble stays visible */
          }
        }
      } catch (err) {
        setMessages((prev) => prev.map((m) =>
          m.id === aiBubbleId ? { ...m, content: `⚠️ ${err.message}`, _isImage: false, _streaming: false } : m
        ));
      } finally {
        pendingAiBubbleRef.current = null;
      }
      return;
    }

    // /edit <prompt>: img2img — requires an attached image
    const EDIT_CMD = /^\/edit\s*/i;
    if (EDIT_CMD.test(text)) {
      const prompt = text.replace(EDIT_CMD, '').trim();
      if (!prompt) return;
      if (attachedFiles.length === 0) {
        // nudge: show a warning bubble so the user knows they need an image
        const warnId = `ai-warn-${Date.now()}`;
        setMessages((prev) => [
          ...prev,
          { id: warnId, _isAi: true, _aiRole: 'assistant', content: t('chat.editNeedsImage'), _streaming: false, created_at: new Date().toISOString(), sender: null },
        ]);
        return;
      }
      const sourceFiles = attachedFiles.map((f) => f.file);
      setInputText('');
      setAttachedFiles([]);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      shouldScrollRef.current = true;

      // Broadcast the command so it appears in all members' history
      sendMessage(text);

      const aiBubbleId = `ai-img-${Date.now()}`;
      pendingAiBubbleRef.current = aiBubbleId;
      setMessages((prev) => [
        ...prev,
        { id: aiBubbleId, _isAi: true, _aiRole: 'assistant', _isImage: true, content: '', _streaming: true, created_at: new Date().toISOString(), sender: null },
      ]);

      try {
        const fd = new FormData();
        sourceFiles.forEach((f) => fd.append('image', f));
        fd.append('prompt', prompt);
        fd.append('size', editImageSize);
        const resp = await fetch(`${getBaseUrl()}/assistant/api/edit-image/`, {
          method: 'POST',
          body: fd,
          credentials: 'include',
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const result = await streamImageResponse(resp, aiBubbleId);
        if (result?.imageUrl) {
          try {
            const savedMsg = await api.post(`chat/rooms/${roomId}/save_ai_image/`, {
              image_url: result.imageUrl,
              prompt: result.imagePrompt || '',
            });
            setMessages((prev) => prev.map((m) => m.id === aiBubbleId ? savedMsg : m));
          } catch (saveErr) {
            console.error('[/edit] save_ai_image failed:', saveErr);
            /* non-blocking — local bubble stays visible */
          }
        }
      } catch (err) {
        setMessages((prev) => prev.map((m) =>
          m.id === aiBubbleId ? { ...m, content: `⚠️ ${err.message}`, _isImage: false, _streaming: false } : m
        ));
      } finally {
        pendingAiBubbleRef.current = null;
      }
      return;
    }

    // Image send: POST multipart to REST endpoint, server broadcasts via channel layer
    if (attachedFiles.length > 0) {
      if (sendingImage) return;
      setSendingImage(true);
      shouldScrollRef.current = true;
      const filesToSend = [...attachedFiles];
      setAttachedFiles([]);
      setInputText('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      try {
        for (const f of filesToSend) {
          const fd = new FormData();
          fd.append('image', f.file);
          if (text) fd.append('caption', text);
          await api.post(`chat/rooms/${roomId}/send_image/`, fd);
        }
      } catch (_) {
        /* non-blocking — WS broadcast will update UI */
      } finally {
        setSendingImage(false);
        textareaRef.current?.focus();
      }
      return;
    }

    if (!text || !connected) return;
    shouldScrollRef.current = true;
    sendMessage(text);
    setInputText('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    textareaRef.current?.focus();
  }, [inputText, attachedFiles, sendingImage, connected, sendMessage, roomId, streamImageResponse, t, user]);

  const handleKeyDown = (e) => {
    // Navigate / select slash command suggestions
    if (slashSuggestions.length > 0) {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashIndex((i) => (i - 1 + slashSuggestions.length) % slashSuggestions.length);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashIndex((i) => (i + 1) % slashSuggestions.length);
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
        e.preventDefault();
        const safeIndex = Math.min(slashIndex, slashSuggestions.length - 1);
        applySlashCommand(slashSuggestions[safeIndex].command);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setInputText('');
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const applySlashCommand = useCallback((command) => {
    // Keep text already typed after the command word, if any
    const afterCmd = inputText.replace(/^\/\S*/, '').trimStart();
    setInputText(command + (afterCmd ? ' ' + afterCmd : ' '));
    setSlashIndex(0);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [inputText]);

  // Clamp slashIndex whenever the suggestions list shrinks (e.g. typing '/as' then '/e')
  useEffect(() => {
    setSlashIndex((i) => (slashSuggestions.length === 0 ? 0 : Math.min(i, slashSuggestions.length - 1)));
  }, [slashSuggestions.length]);

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    // Auto-resize
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
    sendTyping();
  };

  // ── Load more ────────────────────────────────────────────────────────────

  const handleLoadMore = () => {
    shouldScrollRef.current = false;
    setLoadingMore(true);
    loadMessages(page + 1, true);
  };

  // ── Read receipts ─────────────────────────────────────────────────────────

  // Mark all loaded messages as read once the initial fetch finishes.
  // This covers the case where messages were delivered while the room was closed.
  useEffect(() => {
    if (!loading && document.hasFocus()) {
      markRoomRead();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]); // intentionally omit markRoomRead — we only want this on load transition

  // Re-mark as read whenever the window regains focus while this room is open.
  // Covers messages that arrived while the browser tab was in the background.
  useEffect(() => {
    window.addEventListener('focus', markRoomRead);
    return () => window.removeEventListener('focus', markRoomRead);
  }, [markRoomRead]);

  // ── Render ───────────────────────────────────────────────────────────────

  const isAdmin = roomInfo?.is_admin === true;
  const isGroupRoom = roomInfo?.type === 'GROUP';

  const openEditModal = () => {
    setEditName(roomInfo?.name || '');
    setEditDesc(roomInfo?.description || '');
    setEditPhotoFile(null);
    setEditPhotoPreview(roomInfo?.photo_url || null);
    setRemovePhoto(false);
    setEditError(null);
    setShowEditModal(true);
  };

  const handleEditPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditPhotoFile(file);
    setRemovePhoto(false);
    const reader = new FileReader();
    reader.onloadend = () => setEditPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setEditPhotoFile(null);
    setEditPhotoPreview(null);
    setRemovePhoto(true);
  };

  const handleEditSave = async () => {
    setEditSaving(true);
    setEditError(null);
    try {
      await api.patch(`chat/rooms/${roomId}/`, {
        name: editName.trim(),
        description: editDesc.trim(),
      });
      if (editPhotoFile) {
        const fd = new FormData();
        fd.append('photo', editPhotoFile);
        await api.post(`chat/rooms/${roomId}/update_photo/`, fd);
      } else if (removePhoto) {
        await api.post(`chat/rooms/${roomId}/update_photo/`, { remove_photo: true });
      }
      setShowEditModal(false);
      onRoomsRefresh?.();
    } catch (err) {
      setEditError(err?.response?.data?.error || t('common.error'));
    } finally {
      setEditSaving(false);
    }
  };

  const roomName = roomInfo?.display_name || `Room ${roomId}`;
  const memberCount = roomInfo?.members?.length || 0;
  const roomDescription = roomInfo?.description || '';
  const roomPhotoUrl = roomInfo?.photo_url || null;

  const initials = roomName.split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('');

  let lastDateLabel = null;

  return (
    <div
      className={`chat-room-layout${isDragOver ? ' chat-room-drag-over' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* ── Group Edit Modal ───────────────────────────────────────────── */}
      {showEditModal && (
        <div className="chat-modal-backdrop" onClick={() => setShowEditModal(false)}>
          <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chat-modal-header">
              <span>{t('chat.editGroup')}</span>
              <button className="btn-close" onClick={() => setShowEditModal(false)} />
            </div>
            <div className="chat-modal-body">
              <div className="chat-group-photo-edit" onClick={() => editPhotoInputRef.current?.click()}>
                {editPhotoPreview
                  ? <img src={editPhotoPreview} alt="group" className="chat-group-photo-preview" />
                  : <div className="chat-group-photo-placeholder">{initials}</div>
                }
                <div className="chat-group-photo-overlay">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                    <path d="M15 8h.01" />
                    <path d="M12.5 21h-6.5a3 3 0 0 1 -3 -3v-12a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v6.5" />
                    <path d="M18 18m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
                    <path d="M20.2 20.2l1.8 1.8" />
                  </svg>
                </div>
                <input
                  ref={editPhotoInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleEditPhotoChange}
                />
              </div>
              {editPhotoPreview && (
                <button
                  type="button"
                  className="btn btn-sm btn-ghost-danger d-block mx-auto mb-3"
                  onClick={handleRemovePhoto}
                >
                  {t('chat.removePhoto')}
                </button>
              )}
              <div className="mb-3">
                <label className="form-label">{t('chat.groupName')}</label>
                <input
                  className="form-control"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  maxLength={255}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">{t('chat.groupDescription')}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                />
              </div>
              {editError && <div className="alert alert-danger py-2">{editError}</div>}
            </div>
            <div className="chat-modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowEditModal(false)} disabled={editSaving}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" onClick={handleEditSave} disabled={editSaving}>
                {editSaving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="chat-room-header">
        <div className="chat-room-header-avatar">
          {roomPhotoUrl
            ? <img src={roomPhotoUrl} alt={roomName} className="chat-room-header-photo" />
            : initials
          }
        </div>
        <div className="chat-room-header-info">
          <div className="chat-room-header-name">{roomName}</div>
          {roomDescription ? (
            <div className="chat-room-header-sub chat-room-header-desc">{roomDescription}</div>
          ) : memberCount > 0 ? (
            <div className="chat-room-header-sub">
              {t('chat.members', { count: memberCount })}
            </div>
          ) : null}
        </div>
        <div className="d-flex align-items-center gap-1">
          {isGroupRoom && isAdmin && (
            <button
              className="btn btn-sm btn-ghost-secondary chat-edit-btn"
              onClick={openEditModal}
              title={t('chat.editGroup')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" />
                <path d="M13.5 6.5l4 4" />
              </svg>
            </button>
          )}
          {(() => {
            const bg = connected ? COLOR_SUCCESS : COLOR_SECONDARY;
            return (
              <span
                className="badge"
                style={{ fontSize: '0.65rem', backgroundColor: bg, color: getContrastColor(bg) }}
              >
                {connected ? t('chat.online') : t('chat.connecting')}
              </span>
            );
          })()}
        </div>
      </div>

      {/* Messages */}
      <div className="chat-messages">
        {hasMore && (
          <div className="chat-load-more-btn">
            <button
              className="btn btn-sm btn-ghost-secondary"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? (
                <span className="spinner-border spinner-border-sm me-1" />
              ) : null}
              {t('chat.loadMore')}
            </button>
          </div>
        )}

        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ flex: 1 }}>
            <div className="spinner-border text-secondary" role="status" />
          </div>
        ) : messages.length === 0 ? (
          <div className="chat-empty-state">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M8 9h8" /><path d="M8 13h6" />
              <path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12z" />
            </svg>
            <span>{t('chat.noMessages')}</span>
          </div>
        ) : (
          messages.map((msg) => {
            const dateLabel = getDateLabel(msg.created_at);
            const showDivider = dateLabel !== lastDateLabel;
            lastDateLabel = dateLabel;
            const isOwn = msg.sender?.id === user?.id;

            return (
              <React.Fragment key={msg.id}>
                {showDivider && (
                  <div className="chat-date-divider">{dateLabel}</div>
                )}
                <MessageBubble msg={msg} isOwn={isOwn} t={t} />
              </React.Fragment>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Typing indicator */}
      <div className="chat-typing-indicator">
        {typingUsers.size > 0 && t('chat.typing')}
      </div>

      {/* Input */}
      <div className="chat-input-bar">
        {/* Slash command suggestions */}
        {slashSuggestions.length > 0 && (
          <ul className="chat-slash-suggestions">
            {slashSuggestions.map((cmd, i) => (
              <li
                key={cmd.command}
                className={`chat-slash-item${i === slashIndex ? ' chat-slash-item--active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); applySlashCommand(cmd.command); }}
              >
                <span className="chat-slash-cmd">{cmd.command}</span>
                <span className="chat-slash-desc">{t(cmd.description)}</span>
              </li>
            ))}
          </ul>
        )}
        {/* Image size picker — /generate */}
        {showSizePicker && (
          <div className="d-flex align-items-center px-2 py-1 gap-2" style={{ fontSize: '0.8rem', borderBottom: '1px solid var(--tblr-border-color)' }}>
            <i className="ti ti-photo-ai text-muted" />
            <span className="text-muted">{t('chat.imageSize')}:</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={imageSize}
              onChange={(e) => setImageSize(e.target.value)}
            >
              <option value="1024x1024">1024×1024</option>
              <option value="1536x1024">1536×1024 (wide)</option>
              <option value="1024x1536">1024×1536 (tall)</option>
            </select>
          </div>
        )}
        {/* Image size picker — /edit */}
        {showEditSizePicker && (
          <div className="d-flex align-items-center px-2 py-1 gap-2" style={{ fontSize: '0.8rem', borderBottom: '1px solid var(--tblr-border-color)' }}>
            <i className="ti ti-wand text-muted" />
            <span className="text-muted">{t('chat.imageSize')}:</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={editImageSize}
              onChange={(e) => setEditImageSize(e.target.value)}
            >
              <option value="1024x1024">1024×1024</option>
              <option value="1536x1024">1536×1024 (wide)</option>
              <option value="1024x1536">1024×1536 (tall)</option>
            </select>
          </div>
        )}
        {/* Attached image previews */}
        {attachedFiles.length > 0 && (
          <div className="chat-attached-files-strip">
            {attachedFiles.map((f) => (
              <div key={f.id} className="chat-attached-thumb">
                <img src={f.dataUrl} alt={f.file.name} />
                <button
                  type="button"
                  className="chat-attached-remove"
                  onClick={() => setAttachedFiles((prev) => prev.filter((x) => x.id !== f.id))}
                  title={t('common.remove')}
                >
                  <i className="ti ti-x" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="chat-input-row">
          {/* Paperclip / attach */}
          <button
            type="button"
            className="btn btn-icon btn-ghost-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={!connected || sendingImage}
            title={t('chat.attachImage')}
          >
            <i className="ti ti-paperclip" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => { handleFilesAdded(e.target.files); e.target.value = ''; }}
          />

          <textarea
            ref={textareaRef}
            rows={1}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={
              attachedFiles.length > 0
                ? t('chat.addCaption')
                : t('chat.typeMessage')
            }
            disabled={!connected || sendingImage}
          />
          <button
            className="btn btn-primary chat-send-btn"
            onClick={handleSend}
            disabled={(!inputText.trim() && attachedFiles.length === 0) || !connected || sendingImage}
            title={t('chat.send')}
          >
            {sendingImage ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M10 14l11 -11" />
                <path d="M21 3l-6.5 18a.55 .55 0 0 1 -1 0l-3.5 -7l-7 -3.5a.55 .55 0 0 1 0 -1l18 -6.5" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Message bubble ──────────────────────────────────────────────────────────

function MessageBubble({ msg, isOwn, t }) {
  // ── AI inline response bubble (local) or persisted SYSTEM message ──────────
  if (msg._isAi || msg.message_type === 'SYSTEM') {
    const isUser = msg._aiRole === 'user';
    return (
      <div className={`chat-message chat-message--${isUser ? 'own' : 'other'}`}>
        {!isUser && (
          <div className="chat-msg-avatar chat-msg-avatar--ai">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M15.5 13a3.5 3.5 0 0 0 -3.5 3.5v1a3.5 3.5 0 0 0 7 0v-1.8" />
              <path d="M8.5 13a3.5 3.5 0 0 1 3.5 3.5v1a3.5 3.5 0 0 1 -7 0v-1.8" />
              <path d="M17.5 16a3.5 3.5 0 0 0 0 -7h-.5" />
              <path d="M19 9.3v-2.8a3.5 3.5 0 0 0 -7 0" />
              <path d="M6.5 16a3.5 3.5 0 0 1 0 -7h.5" />
              <path d="M5 9.3v-2.8a3.5 3.5 0 0 1 7 0v10" />
            </svg>
          </div>
        )}
        <div className="chat-msg-body">
          {!isUser && <span className="chat-msg-sender chat-msg-sender--ai">{t('chat.aiAssistant')}</span>}
          <div className={`chat-msg-bubble${isUser ? '' : ' chat-msg-bubble--ai'}`}>
            {isUser ? (
              <><span className="chat-ai-cmd-tag">{msg._cmd || '/assistant'}</span> {renderContent(msg.content)}</>
            ) : msg._isImage ? (
              msg._imageUrl ? (
                <div className="chat-msg-image-wrapper">
                  <a href={msg._imageUrl} target="_blank" rel="noopener noreferrer">
                    <img
                      src={getThumbUrl(msg._imageUrl, 400)}
                      alt={msg._imagePrompt || 'generated image'}
                      className="chat-msg-image"
                      loading="lazy"
                    />
                  </a>
                  {msg._imagePrompt && <div className="chat-msg-caption chat-msg-caption--ai">{msg._imagePrompt}</div>}
                </div>
              ) : (
                <span className="chat-ai-cursor" aria-hidden="true" />
              )
            ) : (
              <>
                {renderMarkdown(msg.content)}
                {msg._streaming && <span className="chat-ai-cursor" aria-hidden="true" />}
              </>
            )}
          </div>
          {/* Action bar below the bubble */}
          {!isUser && !msg._streaming && (
            <div className="chat-ai-action-bar">
              {msg._isImage && msg._imageUrl && <ChatDownloadButton url={msg._imageUrl} t={t} />}
              {(msg._isImage ? (msg._imagePrompt || '') : msg.content) && (
                <ChatCopyButton text={msg._isImage ? (msg._imagePrompt || '') : msg.content} t={t} />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Regular bubble ──────────────────────────────────────────────────────
  const sender = msg.sender;
  const senderName = sender?.full_name || sender?.username || t('chat.unknownUser');
  const initials = senderName.split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('');
  const time = msg.created_at
    ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';
  const isImage = msg.message_type === 'IMAGE';

  return (
    <div className={`chat-message chat-message--${isOwn ? 'own' : 'other'}`}>
      {!isOwn && (
        <div className="chat-msg-avatar">
          {sender?.avatar ? (
            <img src={getThumbUrl(sender.avatar, 200)} alt={senderName} />
          ) : (
            <span>{initials}</span>
          )}
        </div>
      )}
      <div className="chat-msg-body">
        {!isOwn && <span className="chat-msg-sender">{senderName}</span>}
        {isImage && msg.image_url ? (
          <div className="chat-msg-image-wrapper">
            <a href={msg.image_url} target="_blank" rel="noopener noreferrer">
              <img
                src={getThumbUrl(msg.image_url, 400)}
                alt={msg.content || 'image'}
                className="chat-msg-image"
                loading="lazy"
              />
            </a>
            {msg.content && <div className="chat-msg-bubble chat-msg-caption">{renderContent(msg.content)}</div>}
          </div>
        ) : (
          <div className="chat-msg-bubble">{renderContent(msg.content)}</div>
        )}
        <span className="chat-msg-time">{time}</span>
      </div>
    </div>
  );
}

function getDateLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'short' });
}

// ── Copy / Download buttons for AI bubbles ───────────────────────────────────

function ChatCopyButton({ text, t }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    let success = false;
    if (navigator.clipboard) {
      try { await navigator.clipboard.writeText(text); success = true; } catch { /* fall through */ }
    }
    if (!success) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
        document.body.appendChild(ta);
        ta.select();
        success = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch { /* silent fail */ }
    }
    if (success) { setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };
  return (
    <button
      type="button"
      className={`chat-ai-action-btn${copied ? ' chat-ai-action-btn--copied' : ''}`}
      onClick={handleCopy}
      title={copied ? t('chat.copied') : t('chat.copy')}
    >
      <i className={`ti ${copied ? 'ti-check' : 'ti-copy'}`} />
    </button>
  );
}

function ChatDownloadButton({ url, t }) {
  const [loading, setLoading] = useState(false);
  const handleDownload = async () => {
    setLoading(true);
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      const ext = url.split('.').pop().split('?')[0] || 'png';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `ai-image.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch { /* silent fail */ }
    setLoading(false);
  };
  return (
    <button
      type="button"
      className="chat-ai-action-btn"
      onClick={handleDownload}
      disabled={loading}
      title={t('chat.downloadImage')}
    >
      <i className={`ti ${loading ? 'ti-loader-2 spin' : 'ti-download'}`} />
    </button>
  );
}

// Render AI/assistant markdown content safely.
function renderMarkdown(content) {
  if (!content) return null;
  const html = DOMPurify.sanitize(marked.parse(content), {
    ADD_TAGS: ['img'],
    ADD_ATTR: ['src', 'alt', 'class'],
    ALLOW_DATA_ATTR: false,
    FORCE_BODY: false,
    // Block tracking pixels and non-local image sources
    ALLOWED_URI_REGEXP: /^(https?:\/\/|\/media\/)/i,
  });
  return <div className="markdown-body" dangerouslySetInnerHTML={{ __html: html }} />;
}

// Split text on URLs and return an array of strings and <a> elements.
const URL_RE = /(https?:\/\/[^\s<>"']+)/g;
function renderContent(text) {
  if (!text) return null;
  const parts = text.split(URL_RE);
  return parts.map((part, i) =>
    URL_RE.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="chat-msg-link"
      >
        {part}
      </a>
    ) : (
      part
    )
  );
}

export default ChatRoom;
